// 10-07 B5 — a mid-test reload keeps the answer the candidate had not saved
// yet (Leo, 2026-10-07: a keep in the tab, NO server write).
//
// Why not the server: a non-provisional save FINALIZES the position (on a
// forward-only test every later write is refused), and `provisional` is
// honoured only in the last seconds before the deadline. So the answer on
// screen lives here, in sessionStorage, between two saves:
//   - written (debounced) on every change the candidate makes;
//   - cleared when a save of that same answer has landed, on Reset (FEAT-18),
//     and — every position of the attempt at once — after the submit, on the
//     time-up card and when the attempt is gone (`bounceToGate`);
//   - restored when the position loads, winning over the server's saved_*
//     (it can only be newer: it is dropped as soon as a save catches up) —
//     but ONLY while the server still holds a copy THIS TAB has seen: one it
//     loaded, or one it saved. Anything else was written elsewhere (EF-2:
//     paused here, resumed and answered on another device, back in this tab),
//     so the server's copy is the newer one and the keep is dropped;
//   - never on a LOCKED position (forward-only, already finalized): its keep
//     is dropped, and the server's copy is shown.
// Never for a file upload — the upload IS the save.
//
// The Gate's "your answers are saved as you go" is what this makes true.
import type { QuestionResponse } from "@/lib/api";
import { cyrb53 } from "@/lib/colors";

export const ANSWER_DRAFT_PREFIX = "answerDraft:v1:";
/** How long a change waits before it is written (the candidate is typing). */
export const ANSWER_DRAFT_DEBOUNCE_MS = 500;

export type AnswerDraft =
  | { kind: "code"; code: string }
  | { kind: "single"; selected: string | null }
  | { kind: "multi"; selected: string[] }
  | { kind: "text"; text: string };

type QuestionType = QuestionResponse["snapshot"]["type"];

export const answerDraftKey = (attemptId: string, position: number): string =>
  `${ANSWER_DRAFT_PREFIX}${attemptId}:${position}`;

/** Which keep a question type holds — null: none (a file upload). PURE. */
export function draftKindOf(type: QuestionType): AnswerDraft["kind"] | null {
  switch (type) {
    case "code":
      return "code";
    // true_false is mcq-shaped; EF-9's likert is single-select too.
    case "mcq":
    case "true_false":
    case "likert":
      return "single";
    case "mcq_multi":
      return "multi";
    case "open_ended":
      return "text";
    default:
      return null;
  }
}

/** Whatever was stored, as a keep of `kind` — or null. PURE. */
export function sanitizeAnswerDraft(
  raw: unknown,
  kind: AnswerDraft["kind"],
): AnswerDraft | null {
  if (!raw || typeof raw !== "object") return null;
  const src = raw as Record<string, unknown>;
  if (src.kind !== kind) return null;
  switch (kind) {
    case "code":
      return typeof src.code === "string" ? { kind, code: src.code } : null;
    case "single":
      return src.selected === null || typeof src.selected === "string"
        ? { kind, selected: src.selected }
        : null;
    case "multi":
      return Array.isArray(src.selected) &&
        src.selected.every((v) => typeof v === "string")
        ? { kind, selected: src.selected as string[] }
        : null;
    case "text":
      return typeof src.text === "string" ? { kind, text: src.text } : null;
  }
}

/**
 * A short print of an answer AS THE SERVER STORES IT — prose stripped (blank
 * is no answer), a multi pick de-duplicated in first-seen order — so the copy
 * this tab saved and the copy the server hands back print the same. PURE.
 */
export function answerPrint(draft: AnswerDraft | { kind: "code"; code: null }): string {
  const value =
    draft.kind === "code"
      ? draft.code
      : draft.kind === "single"
        ? draft.selected
        : draft.kind === "multi"
          ? [...new Set(draft.selected)]
          : draft.text.trim();
  return String(cyrb53(JSON.stringify([draft.kind, value])));
}

/** The print of the copy the server holds for this position — null for a
 *  type with no keep. PURE. */
export function serverAnswerPrint(
  q: Pick<
    QuestionResponse,
    | "saved_selected_answer"
    | "saved_selected_answers"
    | "saved_final_code"
    | "saved_free_text"
  > & { snapshot: { type: QuestionType } },
): string | null {
  switch (draftKindOf(q.snapshot.type)) {
    case "code":
      return answerPrint({ kind: "code", code: q.saved_final_code ?? null });
    case "single":
      return answerPrint({ kind: "single", selected: q.saved_selected_answer ?? null });
    case "multi":
      return answerPrint({ kind: "multi", selected: q.saved_selected_answers ?? [] });
    case "text":
      return answerPrint({ kind: "text", text: q.saved_free_text ?? "" });
    default:
      return null;
  }
}

const seenKey = (attemptId: string, position: number): string =>
  `${answerDraftKey(attemptId, position)}:seen`;
/** Plenty for one position; the oldest print goes first. */
const SEEN_MAX = 50;

function readSeen(attemptId: string, position: number): string[] {
  try {
    const parsed: unknown = JSON.parse(
      window.sessionStorage.getItem(seenKey(attemptId, position)) ?? "[]",
    );
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === "string") : [];
  } catch {
    return [];
  }
}

/** This tab has seen the server hold `print` for the position: it loaded it,
 *  or it is saving it. A keep stays restorable only over such a copy. */
export function noteServerAnswer(
  attemptId: string,
  position: number,
  print: string,
): void {
  const seen = readSeen(attemptId, position);
  if (seen.includes(print)) return;
  seen.push(print);
  try {
    window.sessionStorage.setItem(
      seenKey(attemptId, position),
      JSON.stringify(seen.slice(-SEEN_MAX)),
    );
  } catch {
    // Storage refused: the next load cannot vouch for the keep, and drops it.
  }
}

/**
 * The keep of one position, if there is one of the right kind.
 *
 * With `serverPrint` (what the position's payload holds now), the keep is
 * restored only while that copy is one this tab has seen — otherwise another
 * device wrote it after this keep, and the keep is dropped. The server's copy
 * is then noted as seen, AFTER the check.
 */
export function loadAnswerDraft(
  attemptId: string,
  position: number,
  type: QuestionType,
  serverPrint?: string | null,
): AnswerDraft | null {
  const kind = draftKindOf(type);
  if (kind === null) return null;
  let draft: AnswerDraft | null = null;
  try {
    const raw = window.sessionStorage.getItem(answerDraftKey(attemptId, position));
    draft = raw ? sanitizeAnswerDraft(JSON.parse(raw), kind) : null;
  } catch {
    draft = null;
  }
  if (serverPrint == null) return draft;
  if (
    draft !== null &&
    answerPrint(draft) !== serverPrint &&
    !readSeen(attemptId, position).includes(serverPrint)
  ) {
    clearAnswerDraft(attemptId, position);
    draft = null;
  }
  noteServerAnswer(attemptId, position, serverPrint);
  return draft;
}

export function saveAnswerDraft(
  attemptId: string,
  position: number,
  draft: AnswerDraft,
): void {
  try {
    window.sessionStorage.setItem(
      answerDraftKey(attemptId, position),
      JSON.stringify(draft),
    );
  } catch {
    // Storage refused (full, private mode): the answer still holds for this
    // page load, exactly as before the keep existed.
  }
}

/**
 * Drop the keep of one position. With `onlyIf`, only while it still holds
 * THAT answer: a save lands after the candidate may have changed it again, and
 * the newer keep must survive until its own save.
 */
export function clearAnswerDraft(
  attemptId: string,
  position: number,
  onlyIf?: AnswerDraft,
): void {
  const key = answerDraftKey(attemptId, position);
  try {
    if (onlyIf !== undefined) {
      const raw = window.sessionStorage.getItem(key);
      if (raw !== null && raw !== JSON.stringify(onlyIf)) return;
    }
    window.sessionStorage.removeItem(key);
  } catch {
    // Nothing to do: storage that cannot be read holds nothing to restore.
  }
}

/** The attempt is over (submitted, timed out, gone): every keep of it goes. */
export function clearAttemptAnswerDrafts(attemptId: string): void {
  const prefix = `${ANSWER_DRAFT_PREFIX}${attemptId}:`;
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const key = window.sessionStorage.key(i);
      if (key?.startsWith(prefix)) keys.push(key);
    }
    for (const key of keys) window.sessionStorage.removeItem(key);
  } catch {
    // As above.
  }
}

export interface AnswerDraftWriter {
  /** Write `draft` for the position after the debounce (replaces a pending one). */
  schedule(attemptId: string, position: number, draft: AnswerDraft): void;
  /** Write the pending keep now (a reload, the position changing). */
  flush(): void;
  /** Forget the pending keep without writing it (Reset, a save that wrote it). */
  cancel(): void;
}

export function createAnswerDraftWriter(
  delayMs = ANSWER_DRAFT_DEBOUNCE_MS,
): AnswerDraftWriter {
  let pending: { attemptId: string; position: number; draft: AnswerDraft } | null =
    null;
  let timer: number | null = null;
  const stop = (): void => {
    if (timer !== null) window.clearTimeout(timer);
    timer = null;
  };
  const flush = (): void => {
    stop();
    const p = pending;
    pending = null;
    if (p) saveAnswerDraft(p.attemptId, p.position, p.draft);
  };
  return {
    schedule(attemptId, position, draft) {
      // A different position's pending keep is written, never lost.
      if (pending && (pending.attemptId !== attemptId || pending.position !== position)) {
        flush();
      }
      pending = { attemptId, position, draft };
      stop();
      timer = window.setTimeout(flush, delayMs);
    },
    flush,
    cancel() {
      stop();
      pending = null;
    },
  };
}
